/**
 * Remote Resilience Plugin for Terra Nova
 *
 * Recovery and fail-fast behavior for Module Federation remotes that go stale,
 * become unreachable, or answer a `remoteEntry.js` request with non-JS content
 * while a remote redeploys: the client recovers in place, the server fails fast
 * and un-poisons its caches so the next request retries fresh. Unlike
 * `dynamic-remote-plugin` (URL rewriting, gated to local/platypus), this plugin
 * is bundled in EVERY environment so stage/preview/shadow/prod get the same
 * client recovery — see `apps/* /next.config.mjs`.
 *
 * Three hooks (environment gating in the factory below):
 *   - `loadEntryError` (all environments): evicts a failed remote-entry load
 *     from the runtime's global in-flight cache so the next request refetches
 *     instead of replaying the cached rejection until the host process restarts.
 *   - `fetch` (client only): detects an HTML response for a remoteEntry and
 *     turns it into a typed, retryable error instead of `Unexpected token '<'`.
 *   - `errorLoadRemote` (client): evicts the stale container, re-registers with
 *     a cache-busted entry, and retries the load once before falling through to
 *     the host's `RemotePageBoundary`. (server, SSR runtime only): evicts the
 *     failed remote's cached state and rethrows so the failure surfaces instead
 *     of nextjs-mf's silent stub. Not registered during `next build`, where the
 *     stub keeps builds green.
 */
import { createConsoleLogger } from '@/utils/devtools/console-logger';

import { getCurrentEnv, getHostname } from './_shared.js';

/**
 * Thrown when a remoteEntry.js fetch returns non-JS content (e.g. a feature
 * instance mid-redeploy answering with its SPA/HTML 404 page). Evaluating that
 * as JS would throw `Unexpected token '<'`; surfacing a typed error instead lets
 * `errorLoadRemote` evict + retry the remote.
 */
export class RemoteEntryContentError extends Error {
    constructor(url, status, contentType) {
        super(
            `Remote entry at ${url} returned non-JS content (status ${status}, content-type "${contentType}")`,
        );
        this.name = 'RemoteEntryContentError';
        this.url = url;
        this.status = status;
        this.contentType = contentType;
    }
}

/**
 * Set/replace a `t` cache-bust query param on a remote entry URL. Handles both
 * absolute (local/platypus) and relative (higher-env client) entry forms. Applied
 * only on the recovery path (re-registration) so the canonical happy-path entry
 * URL is never altered.
 */
export const applyEntryCacheBust = (entry, bustValue) => {
    if (!entry || typeof entry !== 'string') {
        return entry;
    }
    try {
        const url = new URL(entry);
        url.searchParams.set('t', String(bustValue));
        return url.href;
    } catch {
        const [beforeHash, hash = ''] = entry.split('#');
        const [path, query = ''] = beforeHash.split('?');
        const params = new URLSearchParams(query);
        params.set('t', String(bustValue));
        return `${path}?${params.toString()}${hash ? `#${hash}` : ''}`;
    }
};

/** Maps iterate in insertion order, so the first key is the oldest. */
const trimTrackedMap = (map, max) => {
    while (map.size > max) {
        map.delete(map.keys().next().value);
    }
};

// Suppress repeats of the same failure within a short window so a remote
// incident (every render retrying the same broken remote) can't flood
// stdout -> Datadog/NewRelic. Bounded so a long-lived process can't grow it.
const LOG_DEDUP_WINDOW_MS = 10_000;
const MAX_TRACKED_FAILURES = 100;
const recentFailureLogs = new Map();

const shouldEmitFailureLog = (event, details) => {
    // `step` keeps the eviction-step failures apart, so a purge failure cannot
    // hide a detach failure on the same remote inside one window.
    const target = details?.url ?? details?.id ?? details?.remoteName ?? '';
    const key = `${event}:${details?.step ?? ''}:${target}`;
    const now = Date.now();
    const last = recentFailureLogs.get(key);
    const suppressed = typeof last === 'number' && now - last < LOG_DEDUP_WINDOW_MS;
    // Delete-then-set moves the key to the end of insertion order (LRU), so
    // capacity eviction drops idle keys, never the remote failing right now. A
    // suppressed hit keeps its window anchored at the last emitted log.
    recentFailureLogs.delete(key);
    recentFailureLogs.set(key, suppressed ? last : now);
    trimTrackedMap(recentFailureLogs, MAX_TRACKED_FAILURES);
    return !suppressed;
};

/**
 * Always-on structured failure log (rate-limited per event, eviction step and
 * target).
 *
 * This is a Module Federation runtime plugin (registered via `runtimePlugins`),
 * so it is part of the federation bootstrap and cannot import `appLogger`:
 * appLogger is a `singleton: true` shared module, and importing a shared module
 * into the runtime plugin creates a circular dependency in the federation init
 * graph that breaks the build. console.error is the correct sink here — on the
 * client these reach the browser console and any RUM/console forwarding, and the
 * namespaced console logger is unusable for this because it is cookie-gated and
 * stays silent unless `pplus_logger` is set.
 */
export const logRemoteFailure = (event, details) => {
    if (!shouldEmitFailureLog(event, details)) {
        return;
    }
    try {
        // eslint-disable-next-line no-console
        console.error(`[remote-resilience-plugin] ${event}`, JSON.stringify(details));
    } catch {
        // eslint-disable-next-line no-console
        console.error(`[remote-resilience-plugin] ${event}`, details);
    }
};

const MAX_TRACKED_COUNTERS = 100;
const failureCounters = new Map();

/**
 * Monotonic per-remote counters attached to the rate-limited failure logs. The
 * log dedupe turns any per-event field into a sample (`evictedPages` reads 0
 * when an unlogged failure already drained the cache), while totals only grow,
 * so a monitor taking the derivative loses nothing between emitted logs. The
 * aggregation contract is in .ai-rules/02-architecture/module-federation.md.
 */
const bumpFailureCounters = (remoteName, fields) => {
    const key = remoteName ?? '';
    const counters = failureCounters.get(key) ?? {};
    Object.entries(fields).forEach(([field, amount]) => {
        counters[field] = (counters[field] ?? 0) + amount;
    });
    // Delete-then-set moves the key to the end of insertion order (LRU), so
    // capacity eviction only drops idle keys. Non-remote ids (manifest URLs,
    // share keys) land here too, which is why the map is capped.
    failureCounters.delete(key);
    failureCounters.set(key, counters);
    trimTrackedMap(failureCounters, MAX_TRACKED_COUNTERS);
    return { ...counters };
};

/** Read a remote's currently-registered entry URL from the host instance. */
export const findRemoteEntry = (origin, remoteName) => {
    try {
        const remotes = origin?.options?.remotes ?? [];
        const match = remotes.find((remote) => remote && remote.name === remoteName);
        return match ? match.entry : undefined;
    } catch {
        return undefined;
    }
};

/**
 * Evict a remote's cached container so the next load re-fetches a fresh
 * remoteEntry.js: drop its cached module instances and clear any in-flight /
 * cached remote-entry load promise from the federation globals.
 */
export const evictRemoteContainer = (origin, remoteName, logger) => {
    try {
        const cache = origin?.moduleCache;
        if (cache && typeof cache.forEach === 'function' && typeof cache.delete === 'function') {
            const keysToDelete = [];
            cache.forEach((_value, key) => {
                if (typeof key === 'string' && key.startsWith(remoteName)) {
                    keysToDelete.push(key);
                }
            });
            keysToDelete.forEach((key) => cache.delete(key));
        }
    } catch (error) {
        logger.error(`⚠️ Failed clearing moduleCache for ${remoteName}: ${error?.message}`);
    }

    // The global in-flight cache is keyed by entry URL; match the snake_case
    // remote name and its dashed feature-instance host form (web_mf_x -> web-mf-x).
    const dashedName = remoteName.replace(/_/g, '-');
    try {
        const globalLoading =
            typeof globalThis !== 'undefined'
                ? globalThis.__GLOBAL_LOADING_REMOTE_ENTRY__
                : undefined;
        if (globalLoading && typeof globalLoading === 'object') {
            Object.keys(globalLoading).forEach((key) => {
                if (key.includes(remoteName) || key.includes(dashedName)) {
                    delete globalLoading[key];
                }
            });
        }
    } catch (error) {
        logger.error(
            `⚠️ Failed clearing global remote-entry cache for ${remoteName}: ${error?.message}`,
        );
    }
};

// Structured-log event names, shared by the hooks and any dashboard query.
export const EVENT_ENTRY_EVICTED = 'loadEntryError evicting failed entry';
export const EVENT_ENTRY_RETAINED = 'loadEntryError retaining cached rejection';
export const EVENT_SERVER_NO_STUB = 'errorLoadRemote (server, no stub)';
export const EVENT_EVICTION_STEP_FAILED = 'eviction step failed';
// Eviction steps carried on `eviction step failed`; part of its dedupe key.
export const STEP_PAGE_REQUIRE_CACHE = 'pageRequireCache';
export const STEP_DETACH_CHILDREN = 'detachChildren';
export const STEP_CONTAINER_GLOBAL = 'containerGlobal';
export const STEP_ENTRY_LOAD_CACHE = 'entryLoadCache';

// At most one eviction pass per key per window. This bounds SSR retry traffic
// (within the window the cached rejection replays with no network round trip)
// and keeps concurrent requests from wiping container state another request is
// rebuilding. Recovery after an MFE returns takes at most one window.
const EVICTION_COOLDOWN_MS = 2_000;
const MAX_TRACKED_EVICTIONS = 100;
const recentEvictions = new Map();

const shouldEvictNow = (key) => {
    const now = Date.now();
    const last = recentEvictions.get(key);
    if (typeof last === 'number' && now - last < EVICTION_COOLDOWN_MS) {
        return false;
    }
    recentEvictions.set(key, now);
    trimTrackedMap(recentEvictions, MAX_TRACKED_EVICTIONS);
    return true;
};

export const resetTrackedStateForTests = () => {
    recentFailureLogs.clear();
    recentEvictions.clear();
    failureCounters.clear();
};

// API routes are excluded: they hold GraphQL singletons (the gateway, and the ApolloServer
// an MFE builds at module scope) whose re-creation fans out introspection to every remote,
// and a failed federated page import cannot poison them.
const isEvictablePageModule = (key) =>
    (key.includes('/.next/server/pages/') && !key.includes('/.next/server/pages/api/')) ||
    (key.includes('\\.next\\server\\pages\\') && !key.includes('\\.next\\server\\pages\\api\\'));

const NO_PURGE = Object.freeze({ evictedPages: 0, detachedRefs: 0, purgeRan: false });

/**
 * Drop the purged page modules from every surviving module's `children`. Node
 * pushes a module into its requirer's `children` on load and never removes it
 * on `delete require.cache[id]`, so without this each re-require of a purged
 * page stacks another copy of its module graph behind the requirer. Each entry
 * is guarded on its own so one hostile module cannot stop the pass.
 */
const detachPurgedChildren = (cache, purgedKeys, remoteName) => {
    let detachedRefs = 0;
    let failedEntries = 0;
    let firstError;
    const noteFailure = (error) => {
        failedEntries += 1;
        if (firstError === undefined) {
            firstError = error?.message;
        }
    };
    try {
        Object.keys(cache).forEach((key) => {
            try {
                const mod = cache[key];
                // Read once: a hostile accessor could answer differently per read.
                const children = mod?.children;
                if (!Array.isArray(children)) {
                    return;
                }
                // `filename` is the resolved path Node keys the cache by; `id` matches
                // it except for the main module.
                const kept = children.filter(
                    (child) => !purgedKeys.has(child?.filename ?? child?.id),
                );
                if (kept.length !== children.length) {
                    mod.children = kept;
                    detachedRefs += children.length - kept.length;
                }
            } catch (error) {
                noteFailure(error);
            }
        });
    } catch (error) {
        // The enumeration itself failed; entries already processed stay detached.
        noteFailure(error);
    }
    if (failedEntries > 0) {
        logRemoteFailure(EVENT_EVICTION_STEP_FAILED, {
            step: STEP_DETACH_CHILDREN,
            remoteName,
            failedEntries,
            error: firstError,
        });
    }
    return detachedRefs;
};

/**
 * Purge the Next server's page modules from Node's require cache. A federated
 * page chunk evaluates successfully even when its remote import fails: it
 * exports its async webpack-startup promise, and when that promise rejects,
 * Node's require cache keeps the module, so Next's `loadComponentsImpl` replays
 * the same rejection on every request. Evicting the page makes the next request
 * re-run the startup and retry the remote. The webpack runtime and chunk files
 * hold retryable state and stay cached. Purged pages are also dropped from
 * their requirers' `children`, otherwise each re-require would retain the
 * previous copy. `purgeRan` is false when the cache was unreachable or the scan
 * threw. `cacheOverride` is a unit-test seam.
 */
export const evictServerPageRequireCache = (
    logger,
    cacheOverride = undefined,
    remoteName = undefined,
) => {
    try {
        const cache =
            cacheOverride ??
            // eslint-disable-next-line camelcase
            (typeof __non_webpack_require__ !== 'undefined'
                ? // eslint-disable-next-line camelcase
                  __non_webpack_require__.cache
                : undefined);
        if (!cache) {
            return NO_PURGE;
        }
        const purgedKeys = new Set();
        Object.keys(cache).forEach((key) => {
            if (isEvictablePageModule(key)) {
                delete cache[key];
                purgedKeys.add(key);
            }
        });
        const detachedRefs =
            purgedKeys.size > 0 ? detachPurgedChildren(cache, purgedKeys, remoteName) : 0;
        return { evictedPages: purgedKeys.size, detachedRefs, purgeRan: true };
    } catch (error) {
        // Uses the always-on log: a purge that fails silently leaves routes stuck
        // until pod restart with no other signal.
        logRemoteFailure(EVENT_EVICTION_STEP_FAILED, {
            step: STEP_PAGE_REQUIRE_CACHE,
            remoteName,
            error: error?.message,
        });
        return NO_PURGE;
    }
};

/**
 * Reset nextjs-mf's `moduleGraphDirty` flag, which its errorLoadRemote listener
 * sets earlier in the same emit chain. A `revalidate()` call would read the
 * flag and run `performReload()`, whose `__FEDERATION__.__INSTANCES__` swap
 * makes rebuilt containers install empty chunks until the pod restarts.
 * `revalidate()` is banned by the Remote Freshness rule in
 * .ai-rules/02-architecture/module-federation.md; this keeps a load failure
 * from arming it anyway.
 */
const disarmHotReloadTrigger = () => {
    try {
        if (typeof globalThis !== 'undefined' && globalThis.moduleGraphDirty) {
            globalThis.moduleGraphDirty = false;
        }
    } catch {
        // best-effort
    }
};

/**
 * Delete the remote's container globals so the next attempt re-evaluates
 * remoteEntry into a pristine container. The global can live under
 * `entryGlobalName` when it diverges from the registered name (mirrors
 * upstream performReload's key derivation), so it is captured before the
 * moduleCache entry is evicted.
 */
const deleteContainerGlobals = (loadArgs, remoteName) => {
    let entryGlobalName;
    try {
        entryGlobalName =
            loadArgs?.origin?.moduleCache?.get?.(remoteName)?.remoteInfo?.entryGlobalName;
    } catch {
        // optional refinement only
    }
    try {
        [remoteName, entryGlobalName].forEach((key) => {
            if (key && typeof globalThis !== 'undefined' && key in globalThis) {
                delete globalThis[key];
            }
        });
    } catch (error) {
        logRemoteFailure(EVENT_EVICTION_STEP_FAILED, {
            step: STEP_CONTAINER_GLOBAL,
            remoteName,
            error: error?.message,
        });
    }
};

/**
 * Server-side cleanup after a failed remote load: evict the remote's container
 * state (moduleCache entries, cached entry loads, container globals) and purge
 * the Next server page require cache. A partially initialized container reused
 * after an outage crashes during expose evaluation ("reading 'call'"), after
 * loadRemote resolved, where no federation hook fires, so it has to be
 * prevented here.
 *
 * Destructive steps run only for ids that resolve to a registered remote:
 * `afterResolve` passes a manifest URL as the id (deriving `'https:'`) and
 * `beforeLoadShare` a share key, and evicting on those would sweep every
 * healthy remote's cached entry loads. Returns the purge result; `purgeRan` is
 * false whenever no purge pass ran.
 */
const evictFailedServerRemoteState = (loadArgs, remoteName, logger) => {
    // Cheap, idempotent, and wanted even for non-remote ids.
    disarmHotReloadTrigger();

    const registeredEntry = remoteName ? findRemoteEntry(loadArgs?.origin, remoteName) : undefined;
    if (!registeredEntry) {
        return NO_PURGE;
    }

    // Latched so concurrent requests can't wipe a container another request is
    // rebuilding.
    if (shouldEvictNow(remoteName)) {
        deleteContainerGlobals(loadArgs, remoteName);
        evictRemoteContainer(loadArgs.origin, remoteName, logger);
    }

    // Never latched: a page re-required after a purge caches a fresh rejected
    // startup promise, which replays before any federation hook fires, so a
    // skipped purge would never be retried. Network retries stay bounded by the
    // entry-load latch in evictFailedEntryLoad.
    //
    // Skipped in dev, where decaching server-bundle modules corrupts webpack's dev
    // module registry and HMR owns freshness.
    if (process.env.NODE_ENV === 'development') {
        return NO_PURGE;
    }
    return evictServerPageRequireCache(logger, undefined, remoteName);
};

/**
 * `loadEntryError` hook body. runtime-core caches the entry-load promise in
 * `__GLOBAL_LOADING_REMOTE_ENTRY__` under `name:entry` and never removes a
 * rejected one, so one transient failure would replay on every later request
 * until the process restarts. Returning undefined keeps this request's failure
 * (the runtime's `remoteEntryExports is undefined` assert still throws), and
 * eviction makes the next attempt fetch fresh. `next build` keeps the cached
 * rejection: page-data collection hits the same dead remote once per import
 * site, and the cache makes repeats free.
 */
export const evictFailedEntryLoad = (args, isBuildPhase) => {
    const { globalLoading, uniqueKey, remoteInfo } = args ?? {};
    const remoteName = remoteInfo?.name;
    const willEvict = !isBuildPhase && shouldEvictNow(`entry:${remoteName ?? ''}`);
    const counters = bumpFailureCounters(remoteName, {
        entryFailuresTotal: 1,
        entryEvictionsTotal: willEvict ? 1 : 0,
    });
    const details = {
        remoteName,
        uniqueKey,
        entry: remoteInfo?.entry,
        env: getCurrentEnv(true),
        // Registered in every environment; browser totals reset per page load, so
        // monitors need to split server and client series.
        runtime: typeof window === 'undefined' ? 'server' : 'client',
        ...counters,
    };
    if (!willEvict) {
        // Its own event because monitors count the eviction event as evictions.
        // Staying silent would hide failures during `next build`, where this is the
        // only registered hook.
        logRemoteFailure(EVENT_ENTRY_RETAINED, {
            ...details,
            reason: isBuildPhase ? 'build-phase' : 'cooldown',
        });
        return undefined;
    }
    logRemoteFailure(EVENT_ENTRY_EVICTED, details);
    try {
        if (globalLoading && uniqueKey) {
            delete globalLoading[uniqueKey];
        }
    } catch (error) {
        logRemoteFailure(EVENT_EVICTION_STEP_FAILED, {
            step: STEP_ENTRY_LOAD_CACHE,
            remoteName,
            error: error?.message,
        });
    }
    return undefined;
};

/**
 * Server-runtime `errorLoadRemote`: evict the failed remote's poisoned state
 * and rethrow instead of letting nextjs-mf's stub stand. The stub (renders
 * null, GSSP returns {props:{}}) hides the failure from
 * getRemotePageServerSideProps, and the webpack bundler runtime installs it as
 * a successful factory, cached until the pod restarts. The throw
 * short-circuits the AsyncHook chain for all four emit lifecycles (`onLoad`,
 * `afterResolve`, `beforeLoadShare`, `beforeRequest`). For `afterResolve` and
 * `beforeRequest` that matches an unhandled hook (the runtime throws
 * RUNTIME_003/RUNTIME_004 on undefined). For `beforeLoadShare` the id is a
 * share key: the throw surfaces a share-scope init failure the stub would
 * hide, and the registered-remote guard skips eviction.
 */
export const failServerRemoteLoad = (loadArgs, logger) => {
    const remoteName = String(loadArgs?.id ?? '').split('/')[0];
    const { evictedPages, detachedRefs, purgeRan } = evictFailedServerRemoteState(
        loadArgs,
        remoteName,
        logger,
    );
    // Every field must be a number on every path: one undefined would turn the
    // running total into NaN for the remote's lifetime.
    const counters = bumpFailureCounters(remoteName, {
        serverLoadFailuresTotal: 1,
        evictedPagesTotal: evictedPages,
        purgesPerformedTotal: purgeRan ? 1 : 0,
        detachedRefsTotal: detachedRefs,
    });
    logRemoteFailure(EVENT_SERVER_NO_STUB, {
        id: loadArgs?.id,
        remoteName,
        from: loadArgs?.from,
        lifecycle: loadArgs?.lifecycle,
        env: getCurrentEnv(true),
        host: getHostname(true, logger),
        entry: findRemoteEntry(loadArgs?.origin, remoteName),
        evictedPages,
        detachedRefs,
        ...counters,
        error: loadArgs?.error instanceof Error ? loadArgs.error.message : String(loadArgs?.error),
    });
    throw loadArgs?.error instanceof Error ? loadArgs.error : new Error(String(loadArgs?.error));
};

// Re-entrancy guard: a retry that fails again triggers errorLoadRemote a second
// time; this prevents infinite recovery recursion for the same remote.
const remotesBeingRecovered = new Set();

/**
 * Validate a remote-entry fetch. A feature instance mid-redeploy can answer
 * remoteEntry.js with its SPA/HTML 404 page, which throws `Unexpected token '<'`
 * when evaluated as JS. Detect it and throw a typed error so errorLoadRemote can
 * evict + retry. Non-remote-entry URLs defer to the default loader.
 */
export const validateRemoteEntryFetch = async (url, requestInit) => {
    if (typeof url !== 'string' || !url.includes('remoteEntry')) {
        return undefined;
    }

    const response = await globalThis.fetch(url, requestInit);
    const contentType = response.headers?.get?.('content-type') ?? '';
    if (/javascript|ecmascript|application\/json/i.test(contentType)) {
        return response;
    }

    const probe = await response.clone().text();
    if (probe.trimStart().startsWith('<')) {
        logRemoteFailure('remoteEntry returned HTML', {
            url,
            status: response.status,
            contentType,
        });
        throw new RemoteEntryContentError(url, response.status, contentType);
    }

    return response;
};

/**
 * Recover from a stale/unavailable remote container without a host restart:
 * evict the cached container, re-register with a fresh cache-bust, and retry the
 * load once. Returns the recovered module on success, otherwise undefined so the
 * host's RemotePageBoundary renders a graceful fallback.
 */
export const recoverRemote = async (loadArgs, logger) => {
    const { id, error, from, lifecycle, origin } = loadArgs ?? {};
    const remoteName = String(id ?? '').split('/')[0];
    const isServer = typeof window === 'undefined';

    logRemoteFailure('errorLoadRemote', {
        id,
        remoteName,
        from,
        lifecycle,
        env: getCurrentEnv(isServer),
        host: getHostname(isServer, logger),
        entry: findRemoteEntry(origin, remoteName),
        error: error instanceof Error ? error.message : String(error),
    });

    if (!remoteName || remotesBeingRecovered.has(remoteName)) {
        return undefined;
    }
    if (
        !origin ||
        typeof origin.registerRemotes !== 'function' ||
        typeof origin.loadRemote !== 'function'
    ) {
        return undefined;
    }

    const currentEntry = findRemoteEntry(origin, remoteName);
    if (!currentEntry) {
        return undefined;
    }

    remotesBeingRecovered.add(remoteName);
    try {
        evictRemoteContainer(origin, remoteName, logger);

        const bustedEntry = applyEntryCacheBust(currentEntry, Date.now());
        origin.registerRemotes([{ name: remoteName, entry: bustedEntry }], { force: true });
        logger.info(`♻️ Retrying remote ${id} after eviction (entry: ${bustedEntry})`);

        const recovered = await origin.loadRemote(id, { from: from || 'runtime' });
        if (recovered) {
            return recovered;
        }
    } catch (retryError) {
        logRemoteFailure('errorLoadRemote retry failed', {
            id,
            remoteName,
            error: retryError instanceof Error ? retryError.message : String(retryError),
        });
    } finally {
        remotesBeingRecovered.delete(remoteName);
    }

    return undefined;
};

export default function RemoteResiliencePlugin() {
    // Create logger with namespace - only logs when 'remote-resilience-plugin' is in pplus_logger cookie
    const logger = createConsoleLogger('remote-resilience-plugin');

    const plugin = {
        name: 'terra-nova-remote-resilience-plugin',
    };

    const isServer = typeof window === 'undefined';
    // `typeof process` guard + bracket access mirror _shared.js so bundlers don't
    // inline the value at build time (this factory also executes in client bundles).
    // Next sets NEXT_PHASE=phase-production-build during `next build` (including
    // page-data collection); it is unset under `next start`/SSR runtime.
    const isBuildPhase =
        typeof process !== 'undefined' && process.env?.['NEXT_PHASE'] === 'phase-production-build';

    plugin.loadEntryError = (args) => evictFailedEntryLoad(args, isBuildPhase);

    if (!isServer) {
        // Validate remote-entry fetches so an HTML response (FI mid-redeploy)
        // becomes a typed, retryable error instead of `Unexpected token '<'`.
        plugin.fetch = (url, requestInit) => validateRemoteEntryFetch(url, requestInit);

        // Evict + retry a stale/unavailable remote container so it recovers
        // without a host restart; falls through to the host error boundary.
        plugin.errorLoadRemote = (loadArgs) => recoverRemote(loadArgs, logger);
    } else if (!isBuildPhase) {
        // Not registered during `next build`: AsyncHook keeps the last listener's
        // return value, and page-data collection needs nextjs-mf's stub or the build
        // fails ("can't get the remote").
        plugin.errorLoadRemote = (loadArgs) => failServerRemoteLoad(loadArgs, logger);
    }

    return plugin;
}
